import { useEffect, useState } from "react";
import { Loader2, QrCode, Smartphone } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api, fmtINR } from "@/lib/api";
import { payWithRazorpay } from "@/lib/razorpay";
import { useLang } from "@/context/LanguageContext";

// Shows the dynamic UPI QR and a card/UPI checkout button; resolves through onPaid once Razorpay confirms.
export const RazorpayPay = ({ order, description, onPaid, onClose }) => {
  const { L } = useLang();
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!order) return undefined;
    const t = setInterval(async () => {
      try {
        const { data } = await api.get(`/payments/status/${order.order_id}`);
        if (data.payment_status === "paid") { clearInterval(t); onPaid(data); }
      } catch { /* keep polling */ }
    }, 4000);
    return () => clearInterval(t);
  }, [order, onPaid]);

  const checkout = async () => {
    setBusy(true);
    try { onPaid(await payWithRazorpay(order, description)); } catch { setBusy(false); }
  };

  return (
    <Dialog open={!!order} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm" data-testid="rzp-pay-dialog">
        <DialogHeader>
          <DialogTitle>{L("Pay", "भुगतान")} {order && fmtINR(order.amount / 100)}</DialogTitle>
          <DialogDescription>{L("Scan with any UPI app, or pay by card / UPI / netbanking.", "किसी भी UPI ऐप से स्कैन करें, या कार्ड / UPI / नेटबैंकिंग से भुगतान करें।")}</DialogDescription>
        </DialogHeader>
        {order?.qr?.image_url ? (
          <img src={order.qr.image_url} alt="Razorpay UPI QR code" className="w-full rounded-xl border border-slate-200" data-testid="rzp-qr-image" />
        ) : (
          <div className="rounded-xl border border-dashed border-slate-300 p-6 text-center text-sm text-slate-500" data-testid="rzp-qr-unavailable"><QrCode className="w-8 h-8 mx-auto mb-2 text-slate-400" />{L("QR code is not available for this payment. Use the button below.", "इस भुगतान के लिए QR उपलब्ध नहीं है। नीचे का बटन इस्तेमाल करें।")}</div>
        )}
        <p className="text-xs text-slate-500 flex items-center gap-2" data-testid="rzp-waiting"><Loader2 className="w-3.5 h-3.5 animate-spin" />{L("Waiting for payment… this updates automatically.", "भुगतान की प्रतीक्षा… यह अपने आप अपडेट होगा।")}</p>
        <Button onClick={checkout} disabled={busy} className="w-full rounded-full rs-cta h-11" data-testid="rzp-checkout-btn"><Smartphone className="w-4 h-4 mr-1" />{L("Pay by card / UPI / netbanking", "कार्ड / UPI / नेटबैंकिंग से भुगतान")}</Button>
      </DialogContent>
    </Dialog>
  );
};
